import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import {
  REGULATED_COVERED_ENTITY_VALUES,
  REGULATED_EVIDENCE_REF_MAX,
  REGULATED_VERTICALS,
  type RegulatedCoveredEntity,
  type RegulatedProfileDisplay,
  type RegulatedVertical,
} from '@heyroot/contracts/tenants/entitlements/regulated-profile';
import { Alert } from '@heyroot/ui/primitives/Alert';
import { Button } from '@heyroot/ui/primitives/Button';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Select } from '@heyroot/ui/primitives/Select';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { ConfirmModal } from '@heyroot/backoffice-ui';
import {
  AdminApiError,
  fetchRegulatedProfile,
  queryKeys,
  saveRegulatedProfile,
  type RegulatedProfileCurrent,
  type RegulatedProfileHistoryRow,
  type RegulatedProfileSaveBody,
} from '../../lib/api';

// #15687: where an operator records, corrects and reads a tenant's regulated profile (law firm,
// dental or medical practice, other). The record switches HeyRoot's regulated-industry guards, so the
// section says what the selected profile DOES, and the confirm step names the consequence of the
// specific transition. Exemplars: docs/design-system/exemplars/result-surface.md (state coverage) and
// vibrancy-grids-tables.md (the history table); no direct record-with-history exemplar exists.

const VERTICAL_LABELS: Record<RegulatedVertical, string> = {
  legal: 'Legal',
  dental: 'Dental',
  medical: 'Medical',
  other: 'Other',
};
const COVERED_LABELS: Record<RegulatedCoveredEntity, string> = {
  yes: 'Yes',
  no: 'No',
  unknown: 'Unknown',
};

// What the guards do for the selected vertical (J12 plan section 5.3). Only `legal` (and an unreadable
// record) trips them, and they refuse NEW advance-money flows; existing ones are not unwound.
const LEGAL_EFFECT =
  'Law firm: new retainers, deposits, prepayments, overpay settings, non-invoice requests, VT order charges, recurring plans and quote billing on acceptance are refused, and gift-card issuance (new cards and reloads) is switched off at once. Existing retainers, deposits and plans are not changed: for a live tenant, close its open requests, deposit settings and plans first.';
const NO_GUARDS_EFFECT = 'No guards apply to this vertical today.';

const CONFIRM_TO_LEGAL =
  'Blocks new retainers, deposits, prepayments, overpay settings, recurring plans and quote billing on acceptance for this tenant, and switches gift-card issuance (new cards and reloads) off at once. Existing retainers, deposits and plans are not changed: for a live tenant, close its open requests, deposit settings and plans first.';
const CONFIRM_REMOVE_GUARDS =
  'Removes the earned-fees guards immediately. Retainers, deposits, recurring plans, quote billing on acceptance and gift-card issuance can then be enabled for this tenant.';
const CONFIRM_NO_CHANGE = 'No guards change.';

const SAVED_CACHE_DELAYED =
  'Saved. The guards apply now; some views may take up to 5 minutes to refresh.';

// Same text the BFF sends for a lost response (UPSTREAM_ERROR); used when a gateway answers 502 or 504 instead.
const SAVE_MAY_HAVE_GONE_THROUGH =
  'The save may have gone through, but its response could not be read. Check the change history before trying again.';

// The engine refused a save made on a stale page and wrote nothing (#16378). Fixed copy: the server text is for logs.
const PROFILE_CHANGED =
  "This tenant's profile changed while you were editing. It has been reloaded; choose Correct profile to review it and save again.";

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${formatDate(iso)}, ${hh}:${mm} UTC`;
}

function humanizeBusinessType(slug: string): string {
  const words = slug.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function whoLabel(actor: { uid: string; email: string | null } | null): string {
  if (!actor) return '—';
  return actor.email ?? actor.uid;
}

function changeLabel(changeType: string): string {
  if (changeType === 'grant') return 'Recorded';
  if (changeType === 'limit_change') return 'Corrected';
  return changeType;
}

function sideLabel(side: RegulatedProfileDisplay | null, missing: string): string {
  if (side === null) return missing;
  if (side.unreadable) return 'Unreadable';
  return `${VERTICAL_LABELS[side.vertical]}, covered entity ${COVERED_LABELS[side.coveredEntity].toLowerCase()}`;
}

function changeSummary(row: RegulatedProfileHistoryRow): string {
  return `${sideLabel(row.before, 'Not recorded')} → ${sideLabel(row.after, 'Removed')}`;
}

function saveErrorMessage(e: unknown): string {
  if (e instanceof AdminApiError) {
    if (e.status === 400) return e.message;
    // The engine refused and wrote nothing (#16378); the slug it compares is the page's, so reloading fixes a stale one.
    if (e.code === 'legal_correction_confirmation_required') {
      return "Removing the law-firm protections needs the tenant's slug confirmed. Reload the page and try again.";
    }
    // A 401 can also be a root-api auth failure, so key on the BFF's own code.
    if (e.code === 'UNAUTHORIZED') return "You don't have permission to record profiles.";
    // root-api answered but the BFF could not read its response: the save may have committed,
    // so "Nothing changed" would be false. The BFF's message says to check the history.
    if (e.code === 'UPSTREAM_ERROR') return e.message;
    // A gateway in front of root-api can time out after the write committed and answer 502 or 504 with a JSON body the
    // BFF relays (#16626): the same unknown outcome, so the same words.
    if (e.status === 502 || e.status === 504) return SAVE_MAY_HAVE_GONE_THROUGH;
  }
  // Not an answer from the BFF at all (a dropped connection, an unreadable body): the write may have
  // committed, so never say "Nothing changed". The panel re-reads on every error (onError below).
  if (!(e instanceof AdminApiError)) {
    return "We couldn't confirm the save. Check the change history before trying again.";
  }
  return "Couldn't save. Nothing changed.";
}

type FormValues = {
  vertical: RegulatedVertical | '';
  coveredEntity: RegulatedCoveredEntity;
  evidenceRef: string;
};

function initialValues(current: RegulatedProfileCurrent): FormValues {
  if (current.state === 'recorded') {
    return {
      vertical: current.vertical,
      coveredEntity: current.coveredEntity,
      evidenceRef: current.evidenceRef,
    };
  }
  return { vertical: '', coveredEntity: 'unknown', evidenceRef: '' };
}

function EffectLine({ vertical }: { vertical: RegulatedVertical }) {
  if (vertical === 'legal') return <Alert variant="warning">{LEGAL_EFFECT}</Alert>;
  return <p className="text-body-sm text-muted">{NO_GUARDS_EFFECT}</p>;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <dt className="text-caption text-muted">{label}</dt>
      <dd className="m-0 text-body-sm text-primary break-words">{children}</dd>
    </div>
  );
}

function HistoryTable({ rows }: { rows: RegulatedProfileHistoryRow[] }) {
  return (
    <>
      <div className="hidden sm:block overflow-x-auto rounded-control border border-border">
        <Table dense aria-label="Regulated profile history">
          <TableHead>
            <tr>
              <TableHeaderCell>When</TableHeaderCell>
              <TableHeaderCell>Change</TableHeaderCell>
              <TableHeaderCell>By</TableHeaderCell>
              <TableHeaderCell>Reason</TableHeaderCell>
              <TableHeaderCell>Before to after</TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={`${row.at}-${i}`}>
                <TableCell className="tabular-nums text-caption text-secondary whitespace-nowrap">
                  {formatDateTime(row.at)}
                </TableCell>
                <TableCell className="text-body-sm text-primary">
                  {changeLabel(row.changeType)}
                </TableCell>
                <TableCell className="text-caption text-secondary">{whoLabel(row.actor)}</TableCell>
                <TableCell className="text-body-sm text-secondary">{row.reason ?? '—'}</TableCell>
                <TableCell className="text-body-sm text-secondary">{changeSummary(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ul
        aria-label="Regulated profile history (cards)"
        className="sm:hidden m-0 flex list-none flex-col gap-2 p-0"
      >
        {rows.map((row, i) => (
          <li
            key={`${row.at}-${i}`}
            className="flex flex-col gap-1 rounded-control border border-border p-3"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-body-sm-emphasis text-primary">
                {changeLabel(row.changeType)}
              </span>
              <span className="tabular-nums text-caption text-secondary">
                {formatDateTime(row.at)}
              </span>
            </div>
            <span className="text-body-sm text-secondary">{changeSummary(row)}</span>
            <span className="text-caption text-muted">By {whoLabel(row.actor)}</span>
            {row.reason && <span className="text-body-sm text-secondary">{row.reason}</span>}
          </li>
        ))}
      </ul>
    </>
  );
}

export function RegulatedProfileSection({
  tenantId,
  tenantSlug,
  businessType,
  canWrite,
}: {
  tenantId: string;
  tenantSlug: string;
  businessType: string;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const fieldId = useId();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: queryKeys.regulatedProfile(tenantId),
    queryFn: () => fetchRegulatedProfile(tenantId),
    // 401 and 404 are states of their own, not transient failures worth retrying.
    retry: false,
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormValues>({
    vertical: '',
    coveredEntity: 'unknown',
    evidenceRef: '',
  });
  const [evidenceTouched, setEvidenceTouched] = useState(false);
  // The vertical the operator was looking at when they opened the form. Frozen on purpose: `current` refetches (window
  // focus, and after every failed save), and a re-derived value would let a retry pass the engine's compare-and-set
  // against a record the operator never reviewed (#16378).
  const [editBase, setEditBase] =
    useState<RegulatedProfileSaveBody['expectedFromVertical']>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [profileChanged, setProfileChanged] = useState(false);
  const changedRef = useRef<HTMLDivElement>(null);
  // When the form closes on a stale-page refusal its focused Save button unmounts; move focus to the warning so it is read out.
  useEffect(() => {
    if (profileChanged) changedRef.current?.focus();
  }, [profileChanged]);

  const save = useMutation({
    mutationFn: (body: RegulatedProfileSaveBody) => saveRegulatedProfile(tenantId, body),
    onSuccess: async (result) => {
      // Re-read BEFORE closing the form: the mutation stays pending ("Saving…") until the fresh
      // view lands, so the panel never shows the pre-save record beside "Saved.".
      await queryClient.invalidateQueries({ queryKey: queryKeys.regulatedProfile(tenantId) });
      setEditing(false);
      setSaveError(null);
      setNotice(result.cacheRefreshDelayed ? SAVED_CACHE_DELAYED : 'Saved.');
    },
    onError: (e) => {
      if (e instanceof AdminApiError && e.code === 'regulated_profile_changed') {
        // A stale page: the record was changed after this form opened. Close the form, so the operator reopens it from
        // the reloaded record; a retry from this form would carry edits made against the old one (#16378).
        setEditing(false);
        setSaveError(null);
        setNotice(null);
        setProfileChanged(true);
      } else {
        setSaveError(saveErrorMessage(e));
      }
      // Refetch anyway: a failure whose outcome is unknown (a lost response, a dropped
      // connection) may still have committed, and the panel must show what the guards now read.
      void queryClient.invalidateQueries({ queryKey: queryKeys.regulatedProfile(tenantId) });
    },
  });

  const heading = <h2 className="text-eyebrow uppercase text-muted mb-3">Regulated profile</h2>;
  const shell = 'rounded-control border border-border bg-surface-elevated p-5';

  if (isPending) {
    return (
      <section className={shell} aria-busy="true">
        {heading}
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-[85%]" />
          <Skeleton className="h-10 w-[65%]" />
        </div>
      </section>
    );
  }

  if (isError) {
    if (error instanceof AdminApiError && error.code === 'UNAUTHORIZED') {
      return (
        <section className={shell}>
          {heading}
          <p className="text-body-sm text-muted">
            You don&apos;t have access to regulated profiles.
          </p>
        </section>
      );
    }
    if (error instanceof AdminApiError && error.status === 404) {
      return (
        <section className={shell}>
          {heading}
          <p className="text-body-sm text-muted">This tenant no longer exists.</p>
          <Link
            to="/tenants"
            className="mt-3 inline-flex items-center gap-1 text-body-sm text-secondary no-underline hover:text-primary transition-colors duration-[var(--duration-fast)]"
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Back to tenants
          </Link>
        </section>
      );
    }
    return (
      <section className={shell}>
        {heading}
        <ErrorState
          title="Couldn't load the regulated profile."
          retryLabel="Retry"
          onRetry={() => void refetch()}
        />
      </section>
    );
  }

  const { current, history } = data;
  const initial = initialValues(current);
  const evidence = form.evidenceRef.trim();
  const changed =
    form.vertical !== initial.vertical ||
    form.coveredEntity !== initial.coveredEntity ||
    evidence !== initial.evidenceRef;
  const valid =
    form.vertical !== '' && evidence.length >= 1 && evidence.length <= REGULATED_EVIDENCE_REF_MAX;
  const canSave = changed && valid && !save.isPending;
  const evidenceError =
    evidenceTouched && evidence.length === 0 ? 'Enter an evidence reference.' : null;

  // What this page loaded, in the engine's vocabulary: its compare-and-set refuses a save made on a stale page.
  const loadedVertical: NonNullable<RegulatedProfileSaveBody['expectedFromVertical']> =
    current.state === 'recorded'
      ? current.vertical
      : current.state === 'unreadable'
        ? 'unknown'
        : 'not_recorded';
  // The vertical the open form is based on: what it was OPENED on, not the refetching `current`.
  const formBase = editBase ?? loadedVertical;
  // The transition decides what the confirm step says (and how hard it is to confirm). It follows the same base the
  // save sends, so a refetch under an open form can never lower the confirm tier (#16378).
  const fromGuarded = formBase === 'legal' || formBase === 'unknown';
  const confirmKind =
    form.vertical === 'legal' ? 'to_legal' : fromGuarded ? 'remove_guards' : 'no_change';

  function startEditing() {
    setForm(initialValues(current));
    setEditBase(loadedVertical);
    setEvidenceTouched(false);
    setSaveError(null);
    setNotice(null);
    setProfileChanged(false);
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setSaveError(null);
  }

  function submitWithReason(reason: string) {
    if (form.vertical === '') return;
    setConfirmOpen(false);
    setSaveError(null);
    save.mutate({
      reason,
      regulatedProfile: {
        vertical: form.vertical,
        coveredEntity: form.coveredEntity,
        evidenceRef: evidence,
      },
      expectedFromVertical: formBase,
      // Sent only where the operator actually typed it: the remove-guards confirm is the one place that asks.
      ...(confirmKind === 'remove_guards' ? { confirmSlug: tenantSlug } : {}),
    });
  }

  const actionLabel =
    current.state === 'recorded' || current.state === 'unreadable'
      ? 'Correct profile'
      : 'Record profile';

  return (
    <section className={shell}>
      {heading}

      {current.state === 'not_recorded' && (
        <div className="flex flex-col gap-1">
          <p className="text-body-sm text-secondary">
            Not recorded. Until a profile is recorded, HeyRoot applies no regulated-industry limits
            to this tenant. Record one before boarding a law firm, or a medical or dental practice.
          </p>
          <p className="text-caption text-muted">
            Business type: {humanizeBusinessType(businessType)}, not evidence
          </p>
        </div>
      )}

      {current.state === 'unreadable' && (
        <Alert variant="warning">
          This record can&apos;t be read. The guards treat it as a law firm (fail closed).
        </Alert>
      )}

      {current.state === 'recorded' && (
        <div className="flex flex-col gap-3">
          <dl className="m-0 grid gap-3 sm:grid-cols-3">
            <Fact label="Vertical">{VERTICAL_LABELS[current.vertical]}</Fact>
            <Fact label="Covered entity">{COVERED_LABELS[current.coveredEntity]}</Fact>
            <Fact label="Evidence reference">{current.evidenceRef || '—'}</Fact>
          </dl>
          <EffectLine vertical={current.vertical} />
          <p className="text-caption text-muted">
            Recorded by {whoLabel(current.attestedBy)} on {formatDate(current.attestedAt)}
          </p>
        </div>
      )}

      {notice && (
        <p role="status" className="mt-3 text-body-sm text-success-text">
          {notice}
        </p>
      )}

      {profileChanged && (
        <div ref={changedRef} tabIndex={-1} className="mt-3 outline-none">
          <Alert variant="warning">{PROFILE_CHANGED}</Alert>
        </div>
      )}

      {canWrite && !editing && (
        <div className="mt-4">
          <Button variant="secondary" onClick={startEditing}>
            {actionLabel}
          </Button>
        </div>
      )}

      {canWrite && editing && (
        <form
          aria-label="Regulated profile form"
          className="mt-4 flex flex-col gap-4 border-t border-border pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) setConfirmOpen(true);
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor={`${fieldId}-vertical`}
                className="text-body-sm-emphasis text-secondary"
              >
                Vertical
              </label>
              <Select
                id={`${fieldId}-vertical`}
                value={form.vertical}
                onChange={(e) =>
                  setForm((f) => ({ ...f, vertical: e.target.value as RegulatedVertical | '' }))
                }
              >
                <option value="" disabled>
                  Choose a vertical
                </option>
                {REGULATED_VERTICALS.map((v) => (
                  <option key={v} value={v}>
                    {VERTICAL_LABELS[v]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor={`${fieldId}-covered`}
                className="text-body-sm-emphasis text-secondary"
              >
                Covered entity
              </label>
              <Select
                id={`${fieldId}-covered`}
                value={form.coveredEntity}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    coveredEntity: e.target.value as RegulatedCoveredEntity,
                  }))
                }
              >
                {REGULATED_COVERED_ENTITY_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {COVERED_LABELS[v]}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {form.vertical !== '' && <EffectLine vertical={form.vertical} />}

          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${fieldId}-evidence`} className="text-body-sm-emphasis text-secondary">
              Evidence reference
            </label>
            <Input
              id={`${fieldId}-evidence`}
              value={form.evidenceRef}
              maxLength={REGULATED_EVIDENCE_REF_MAX}
              invalid={evidenceError !== null}
              aria-describedby={`${fieldId}-evidence-note`}
              onChange={(e) => setForm((f) => ({ ...f, evidenceRef: e.target.value }))}
              onBlur={() => setEvidenceTouched(true)}
            />
            {evidenceError ? (
              <span id={`${fieldId}-evidence-note`} className="text-body-sm text-danger-text">
                {evidenceError}
              </span>
            ) : (
              <span id={`${fieldId}-evidence-note`} className="text-body-sm text-muted">
                Where the classification was confirmed, such as a boarding call or engagement letter
                (1 to {REGULATED_EVIDENCE_REF_MAX} characters).
              </span>
            )}
          </div>

          {saveError && (
            <div role="alert" className="flex flex-wrap items-center gap-3">
              <span className="text-body-sm text-danger-text">{saveError}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={!canSave}
              >
                Retry
              </Button>
            </div>
          )}

          <div className="flex items-center gap-2.5">
            <Button type="submit" variant="primary" disabled={!canSave}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="ghost" onClick={cancelEditing} disabled={save.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {}}
        onConfirmWithReason={submitWithReason}
        reasonRequired
        reasonMinLength={1}
        reasonMaxLength={500}
        destructive={confirmKind === 'remove_guards'}
        {...(confirmKind === 'remove_guards' ? { confirmText: tenantSlug } : {})}
        // The modal turns the typed confirm OFF for an empty confirmText, so an empty slug would leave a plain button.
        confirmDisabled={confirmKind === 'remove_guards' && tenantSlug === ''}
        confirmLabel={
          confirmKind === 'to_legal'
            ? 'Record'
            : confirmKind === 'remove_guards'
              ? 'Remove guards'
              : 'Save'
        }
        title={
          confirmKind === 'to_legal'
            ? 'Record this tenant as a law firm?'
            : confirmKind === 'remove_guards'
              ? 'Remove the legal guards for this tenant?'
              : 'Save this regulated profile?'
        }
      >
        <p className="text-body-sm text-secondary">
          {confirmKind === 'to_legal'
            ? CONFIRM_TO_LEGAL
            : confirmKind === 'remove_guards'
              ? CONFIRM_REMOVE_GUARDS
              : CONFIRM_NO_CHANGE}
        </p>
      </ConfirmModal>

      <div className="mt-6 flex flex-col gap-2">
        <h3 className="text-body-sm-emphasis text-secondary">Change history</h3>
        {history.rows.length === 0 ? (
          <p className="text-body-sm text-muted">No changes recorded.</p>
        ) : (
          <>
            <HistoryTable rows={history.rows} />
            {history.total > history.rows.length && (
              <p className="text-caption text-muted">
                Showing the latest {history.rows.length} of {history.total}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
